import { sendAnalytics } from '@audio-network/analytics'
import {
	Button,
	Modal,
	ModalActions,
	ModalContent,
	ModalHeading,
	TextArea,
	TextInput,
} from '@audio-network/componentlibrary'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import useProjectStore from '@/stores/projectStore'
import { toast } from '@/utils/notifications/toast'

import styles from './NewProjectModal.module.css'
import { NewProjectModalProps } from './NewProjectModal.types'

const NewProjectModal = ({ id }: NewProjectModalProps): JSX.Element => {
	const { t } = useTranslation(['navigation'])
	const addProject = useProjectStore((state) => state.addProject)
	const newProjectModalRef = useRef(null)
	const router = useRouter()
	const { register, handleSubmit, reset } = useForm<{
		projectName: string
		projectDescription: string
	}>({
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
	})

	const handleNewProjectSubmit: SubmitHandler<{
		projectName: string
		projectDescription: string
	}> = async (data): Promise<void> => {
		const { projectName, projectDescription } = data
		const newProjectName = projectName ? projectName : t('new-project-untitled')
		const project = await addProject(newProjectName, projectDescription)
		if (project) {
			sendAnalytics({
				noun: 'Create Project',
				pastTenseVerb: 'Completed',
			})
			reset()
			router.push(`/project/edit/${project.Id}`)
		} else {
			toast.error(t('new-project-create-project-error'), {
				'data-testid': 'new-project-create-project-error',
			})
		}

		newProjectModalRef.current?.close()
	}

	const newProjectModalHeadingId = `new-project-modal-heading-${id}`
	const newProjectFormId = `new-project-form-${id}`

	return (
		<Modal
			aria-labelledby={newProjectModalHeadingId}
			id={id}
			ref={newProjectModalRef}
		>
			<ModalHeading id={newProjectModalHeadingId}>
				{t('new-project-heading')}
			</ModalHeading>
			<ModalContent>
				<form
					className={styles.newProjectForm}
					id={newProjectFormId}
					noValidate
					onSubmit={handleSubmit(handleNewProjectSubmit)}
				>
					<TextInput
						name="projectName"
						label={t('new-project-project-name')}
						{...register('projectName')}
					/>
					<TextArea
						name="projectDescription"
						label={t('new-project-project-description')}
						rows={3}
						className={styles.newProjectFormDescription}
						{...register('projectDescription')}
					/>
				</form>
			</ModalContent>
			<ModalActions>
				<Button
					form={newProjectFormId}
					size="S"
					type="submit"
					variant="primary"
				>
					{t('new-project-create-project')}
				</Button>
			</ModalActions>
		</Modal>
	)
}

export default NewProjectModal
